// UnfreezeApplication.js - Page for unfreezing application sections
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Paper,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Snackbar,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  LockOpen as UnfreezeIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import UnfreezeApiService from "../../../services/UnfreezeApiService";
import { decryptAppId, decryptText } from "../../../utils/encryption";
import { format } from "date-fns";

const UnfreezeApplication = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sections, setSections] = useState([]);
  const [unfreezeRecords, setUnfreezeRecords] = useState([]);
  const [selectedSection, setSelectedSection] = useState("");
  const [expiryDate, setExpiryDate] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [submitting, setSubmitting] = useState(false);

  // Router hooks
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get encrypted parameters from URL
  const encryptedAppId = searchParams.get("appId");
  const encryptedOrgId = searchParams.get("orgId");

  // Decrypt parameters
  const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const orgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;

  // Get current user ID from localStorage (adjust based on your auth implementation)
  const currentUserId = localStorage.getItem("orgId") || "1"; // Default fallback

 

  // Load unfreeze records
  const loadUnfreezeRecords = useCallback(async () => {
    if (!appId) return;

    try {
      const response = await UnfreezeApiService.getUnfreezeRecords(appId, orgId);
      if (response.success) {
        setUnfreezeRecords(response.data);
      }
    } catch (err) {
      console.error("Load unfreeze records error:", err);
    }
  }, [appId, orgId]);

  // Available sections for unfreezing
  const availableSections = [
    'Adverse Action History',
    'Category of Medical Testing Facility',
    'Collection of Sample From',
    'Contact Person from laboratory for NABL',
    'Declaration Section',
    'Equipment',
    'Internal Audit and Management Review',
    'Laboratory Details',
    'Legal Entity Type',
    'NABL Accreditation Consultancy',
    'Non-Analytical Instruments',
    'Organization Details',
    'Parent Organization',
    'Proficiency Testing',
    'Proficiency Testing Document',
    'Reference Material',
    'Scope',
    'Discipline',
    'SEZ Region',
    'Lab Staff',
    'Type of Functioning'
  ];

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      if (!appId || !orgId) {
        setError("Missing required parameters: Application ID or Organization ID");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await loadUnfreezeRecords();
      } catch (err) {
        console.error("Error loading initial data:", err);
        setError("Failed to load application data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [appId, orgId, loadUnfreezeRecords]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!selectedSection || !expiryDate) {
      showSnackbar("Please select a section and expiry date", "error");
      return;
    }

    try {
      setSubmitting(true);

      const unfreezeData = {
        appId: parseInt(appId),
        orgId: parseInt(orgId),
        sectionName: selectedSection,
        ifFreezed: 0, // 0 = unfrozen
        expiryDate: format(expiryDate, "yyyy-MM-dd HH:mm:ss"),
        userId: parseInt(currentUserId),
      };

      if (editingRecord) {
        // Update existing record
        await UnfreezeApiService.updateUnfreezeRecord(editingRecord.id, unfreezeData);
        showSnackbar("Unfreeze record updated successfully", "success");
      } else {
        // Create new record
        await UnfreezeApiService.createUnfreezeRecord(unfreezeData);
        showSnackbar("Section unfrozen successfully", "success");
      }

      // Reset form and reload data
      resetForm();
      await loadUnfreezeRecords();
    } catch (err) {
      showSnackbar(`Error: ${err.response?.data?.message || err.message}`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit
  const handleEdit = (record) => {
    setEditingRecord(record);
    setSelectedSection(record.sectionname);
    setExpiryDate(new Date(record.expirydate));
    setOpenDialog(true);
  };

  // Handle delete
  const handleDelete = async (record) => {
    if (!window.confirm("Are you sure you want to delete this unfreeze record?")) {
      return;
    }

    try {
      await UnfreezeApiService.deleteUnfreezeRecord(record.id, parseInt(currentUserId));
      showSnackbar("Unfreeze record deleted successfully", "success");
      await loadUnfreezeRecords();
    } catch (err) {
      showSnackbar(`Error deleting record: ${err.response?.data?.message || err.message}`, "error");
    }
  };

  // Reset form
  const resetForm = () => {
    setSelectedSection("");
    setExpiryDate(null);
    setEditingRecord(null);
    setOpenDialog(false);
  };

  // Show snackbar
  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle back navigation
  const handleBackClick = () => {
    navigate(-1);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    } catch {
      return dateString;
    }
  };

  // Check if expiry date is in the past
  const isExpired = (dateString) => {
    return new Date(dateString) < new Date();
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Unfreeze Application...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={handleBackClick}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f8fafc",
          pt: 2,
          pb: 4,
        }}
      >
        {/* Header with Gradient Background */}
        <Paper
          elevation={0}
          sx={{
            background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
            color: "white",
            borderRadius: 0,
            mb: 3,
          }}
        >
          <Box sx={{ px: 3, py: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
              sx={{
                mb: 2,
                "& .MuiBreadcrumbs-separator": { color: "rgba(255,255,255,0.7)" },
              }}
            >
              <Link
                component="button"
                variant="body2"
                onClick={handleBackClick}
                sx={{
                  textDecoration: "none",
                  color: "rgba(255,255,255,0.8)",
                  "&:hover": { color: "white" },
                }}
              >
                Dashboard
              </Link>
              <Typography variant="body2" sx={{ color: "white" }}>
                Unfreeze Application
              </Typography>
            </Breadcrumbs>

            {/* Header Content */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Avatar
                  sx={{
                    bgcolor: "rgba(255,255,255,0.2)",
                    mr: 2,
                    width: 56,
                    height: 56,
                  }}
                >
                  <UnfreezeIcon sx={{ fontSize: 28 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Unfreeze Application Sections
                  </Typography>
                  <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                    Application ID: {appId} • Organization ID: {orgId}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackClick}
                sx={{
                  borderColor: "rgba(255,255,255,0.3)",
                  color: "white",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Back
              </Button>
            </Box>
          </Box>
        </Paper>

        <Box sx={{ px: 3 }}>
          {/* Add New Unfreeze Record Card */}
          <Card sx={{ mb: 4, borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: "text.primary" }}>
                Unfreeze Section
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Select Section</InputLabel>
                    <Select
                      value={selectedSection}
                      label="Select Section"
                      onChange={(e) => setSelectedSection(e.target.value)}
                      disabled={submitting}
                    >
                      {availableSections.map((section, index) => (
                        <MenuItem key={index} value={section}>
                          {section}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <DateTimePicker
                    label="Unfreeze Expiry Date & Time"
                    value={expiryDate}
                    onChange={(newValue) => setExpiryDate(newValue)}
                    disabled={submitting}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    minDateTime={new Date()}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={submitting ? <CircularProgress size={16} /> : <SaveIcon />}
                      onClick={handleSubmit}
                      disabled={submitting || !selectedSection || !expiryDate}
                      sx={{
                        py: 1.5,
                        px: 3,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                      }}
                    >
                      {submitting ? "Processing..." : editingRecord ? "Update" : "Unfreeze Section"}
                    </Button>

                    {editingRecord && (
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={resetForm}
                        disabled={submitting}
                        sx={{
                          py: 1.5,
                          px: 3,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                        }}
                      >
                        Cancel Edit
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Unfreeze Records Table */}
          <Card sx={{ borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: "1px solid #e2e8f0" }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: "text.primary" }}>
                  Unfreeze Records
                </Typography>
              </Box>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 600 }}>Section Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Expiry Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unfrozen By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unfreezeRecords.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No unfreeze records found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      unfreezeRecords.map((record) => (
                        <TableRow key={record.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {record.sectionname}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                isExpired(record.expirydate)
                                  ? "Expired"
                                  : record.iffreezed === 0
                                  ? "Unfrozen"
                                  : "Frozen"
                              }
                              color={
                                isExpired(record.expirydate)
                                  ? "error"
                                  : record.iffreezed === 0
                                  ? "success"
                                  : "default"
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(record.expirydate)}
                            </Typography>
                            {isExpired(record.expirydate) && (
                              <Typography variant="caption" color="error">
                                Expired
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {record.LoginName || `User ${record.unfreezedby}`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(record.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(record)}
                                sx={{ color: "primary.main" }}
                                title="Edit"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(record)}
                                sx={{ color: "error.main" }}
                                title="Delete"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default UnfreezeApplication;